import {isEqual, reduce} from 'lodash'
import {ID_KEY} from '../trans/transerializable'

const DEFAULTED_KEY = `__@@__defaulted_props`

export function isDefaultedEqual(o1: object, o2: object) {
  /*
   *  While the defaulted map is not enumerable, it is checked
   *  by lodash as lodash doesn't just operate over the Object.keys
   *  call but instead looks at
   */
  const filterDefaultMap = <T extends object>(obj: T): T =>
    reduce<string, T>(
      Object.keys(obj),
      (acc: T, key: string) => {
        acc[key] = obj[key]
        return acc
      }, {} as T
    )
  return isEqual(
    filterDefaultMap(o1),
    filterDefaultMap(o2)
  )
}

function getDefaulted(obj: any) {
  return obj[DEFAULTED_KEY]
}

function isDefaultedObject(obj: any) {
  const defaulted = getDefaulted(obj)
  return defaulted && typeof defaulted === 'object'
}

export function setDefault(obj: any, key: string) {
  if (!getDefaulted(obj)) {
    Object.defineProperty(obj, DEFAULTED_KEY, { value: {}, enumerable: false})
  }
  if (isDefaultedObject(obj)) {
    getDefaulted(obj)[key] = true
  }
}

export function unsetDefault(obj: any, key: string) {
  if (isDefaultedObject(obj)) {
    getDefaulted(obj)[key] = false
  }
}

export function isDefault(obj: any, key: string): boolean {
  return isDefaultedObject(obj) && getDefaulted(obj)[key]
}
/**
 * Subtly different than "isDefault", this will return true
 * if the key is undecorated as defaultable.
 *
 * @param obj Object to check
 * @param key Key in object
 */
export function isDefaultOrStompable(obj: any, key: string): boolean {
  if (isDefaultedObject(obj) && getDefaulted(obj)[key] === undefined) {
    return true
  }
  if (!isDefaultedObject(obj)) {
    return true
  }
  return isDefault(obj, key)
}

export function setAllDefaults<T extends object>(obj: T) {
  setDefaults(obj, Object.keys(obj))
}

export function setDefaults<T extends object>(obj: T, keys: string[] | string) {
  [].concat(keys).forEach(
    key => setDefault(obj, key)
  )
}

function findKeyOrThis(obj: object): any | number {
  return (obj[ID_KEY]) ? obj[ID_KEY] : obj
}

export type Mapper<T> = {
  (value: any): T
}

export function propertyMapper<T>(
  prototype: any,
  key: string,
  mapper: Mapper<T>
) {
  const values = new Map<any, T>()
  Object.defineProperty(
    prototype,
    key,
    {
      set(firstValue: any) {
        Object.defineProperty(this, key, {
          get() {
            return values.get(findKeyOrThis(this))
          },
          set(value: any) {
            if (isDefault(this, key)) {
              unsetDefault(this, key)
            } else {
              setDefault(this, key)
            }
            values.set(findKeyOrThis(this), mapper(value))
          },
          enumerable: true
        })
        this[key] = firstValue
      },
      enumerable: true,
      configurable: true
    }
  )
}

export function defaultProperty(): any {
  return function(target: any, key: string) {
    propertyMapper(target, key, (value: any) => value)
  }
}
